/**
 * Recovery from stale code-split chunks after a deploy.
 *
 * Every page is a lazy chunk with a content-hashed name. An installed PWA can sit in the background for days with
 * the old index.js in memory; after a new image is deployed the old chunk files are gone, so opening a page
 * that was never loaded rejects its dynamic import (React.lazy caches that rejection, so only a full reload
 * recovers). We reload once automatically; a timestamp in sessionStorage keeps that from looping when the
 * failure is something a reload doesn't fix (server down, offline).
 */

const RELOAD_KEY = 'taretto:chunk-reload-at';
/** A second chunk failure within this window after an automatic reload is shown instead of reloading again. */
export const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed/i;

/** Whether `error` is a failed lazy-chunk load (Chrome, Firefox and Safari word it differently). */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return typeof error === 'string' && CHUNK_ERROR_MESSAGE.test(error);
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_MESSAGE.test(error.message);
}

/** Minimal storage surface (sessionStorage) so the guard can be tested. */
export interface ReloadStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function sessionStore(): ReloadStore | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null; // storage blocked
  }
}

/**
 * Records an automatic reload and returns true, unless one already happened within the cooldown (or storage
 * is unavailable, in which case we can't tell and don't risk a loop).
 */
export function claimChunkReload(store: ReloadStore | null = sessionStore(), now = Date.now()): boolean {
  if (!store) return false;
  try {
    const last = Number(store.getItem(RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    store.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

let reloading = false;

/** True once an automatic reload has started (error screens can say "updating" instead of flashing an error). */
export function isReloadingForStaleChunk(): boolean {
  return reloading;
}

/** Reloads the page for a stale chunk, once per cooldown. Returns whether a reload was started. */
export function reloadForStaleChunk(): boolean {
  if (reloading) return true;
  if (typeof window === 'undefined' || !claimChunkReload()) return false;
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Vite fires `vite:preloadError` when a chunk (or its CSS) fails to preload. Reloading there catches the
 * failure before React renders an error; if we decide not to reload, the error propagates to the route's
 * error element as usual.
 */
let installed = false;

export function installChunkReloadHandler(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) event.preventDefault();
  });
}
